import { EquipmentIcon, PassIcon, ShirtIcon, UserIcon } from "@xola/icons";
import clsx from "clsx";
import { DemographicIcon, isEquipmentItem, isExperiencePurchaseItem, isMembershipItem, isPackageItem } from "common";
import pluralize from "pluralize";
import React from "react";
import { useHistory } from "react-router-dom";
import { LineItemType, Product, ProductReference, Purchase, PurchaseItem, PurchaseItemObject, Unit } from "types";
import { useResource } from "../../hooks/useResource";
import { isDefaultDemographic } from "../../models/DemographicUnit";
import { isMerchandisePurchaseItemFulfilled } from "../../models/MerchandisePurchaseItem";
import { isDisputeSubmitted, isGift } from "../../models/Purchase";
import { NoTranslate } from "../Common/NoTranslate";
import { BalanceBadge } from "../Dashboard/Common/BalanceBadge";
import { PurchaseDisputeBadge } from "./PurchaseDisputeBadge";
import { getPurchaseItemArrivalDate } from "./PurchaseListItem";
import { ItemProductImage } from "./PurchaseProductImage";
import { StatusIcon } from "./PurchaseStatusIcon";
import { usePurchaseDrawerStore } from "@/stores/purchaseDrawerStore";
import { usePurchasePageStore } from "@/stores/usePurchasePageStore";
import { getSeller } from "@/utils/getSeller";

interface PurchaseTileBreakdownProps {
    purchase: Purchase;
    selectedItem?: PurchaseItem;
    isDrawerContext?: boolean;
}

/**
 * Show the breakdown of a MIST purchase. This will show each item in a smaller size
 */
export const PurchaseTileBreakdown = ({
    purchase,
    selectedItem,
    isDrawerContext = false,
}: PurchaseTileBreakdownProps) => {
    return (
        <div className={clsx("bg-gray-lighter py-4 pl-8")}>
            {purchase.items.map((item, index) => (
                <PurchaseTileItem
                    key={item.id}
                    purchase={purchase}
                    selectedItem={selectedItem}
                    item={item}
                    index={index}
                    isDrawerContext={isDrawerContext}
                />
            ))}
        </div>
    );
};

interface PurchaseTileItemProps {
    purchase: Purchase;
    selectedItem?: PurchaseItem;
    item: PurchaseItem;
    index: number;
    isDrawerContext?: boolean;
}

const PurchaseTileItem = ({ purchase, selectedItem, item, index, isDrawerContext = false }: PurchaseTileItemProps) => {
    const history = useHistory();
    const handleClick = () => {
        if (isDrawerContext) {
            const actions = usePurchaseDrawerStore.getState().actions;
            actions.setPurchaseItemId(item.id);
        } else {
            const newItemId = item?.id ?? "";
            // Updating last selected item id in-case user wants to see purchase level info for MIST purchases
            usePurchasePageStore.setState({ lastSelectedItem: newItemId });
            history.push(`/purchases/${purchase.id}/${newItemId}`);
        }
    };

    const itemArrivalDate = getPurchaseItemArrivalDate(item);

    return (
        <div key={item.id} className="relative flex w-[95%] items-start" onClick={handleClick}>
            <ItemBreakdownTreeLine />
            {index === 0 && <VerticalLine count={purchase.items.length} />}

            <div
                className={clsx("flex w-full cursor-pointer flex-row rounded p-0.5 hover:bg-blue-lighter", {
                    "bg-blue-lighter": item.id === selectedItem?.id,
                })}
            >
                <ItemProductImage item={item} size="tiny" className="!mr-1" classNames={{ image: "h-8 w-8" }} />

                <div className="flex w-full flex-col pt-0.5 text-sm">
                    <div className="flex w-full justify-between">
                        <ItemProductName product={item.product} />

                        {itemArrivalDate && (
                            <NoTranslate as="div" className="whitespace-nowrap text-xs">
                                {itemArrivalDate}
                            </NoTranslate>
                        )}
                    </div>

                    <div className="flex flex-wrap items-center space-y-1">
                        <ItemDemographicSummary item={item} />

                        {isDisputeSubmitted(purchase) ? (
                            <PurchaseDisputeBadge />
                        ) : isGift(item) && item?.balance && item?.balance > 0 ? (
                            <BalanceBadge color="success" value={item.balance} className="mr-1" size="small" />
                        ) : null}

                        <StatusIcon skipConfirmed displayText={false} item={item} />
                    </div>
                </div>
            </div>
        </div>
    );
};

const ItemProductName = ({ product }: { product: ProductReference }) => {
    const { data: experience } = useResource<Product>(["products", product.id]);
    return (
        <NoTranslate
            className="w-32 truncate text-sm font-bold"
            data-testid="purchase-tile-breakdown-item-product-name"
        >
            {experience?.name}
        </NoTranslate>
    );
};

/**
 * The little "L" line to show a tree for the PurchaseItem
 */
const ItemBreakdownTreeLine = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        className="relative -top-3 left-[4.1px]"
        width="16"
        height="34"
        fill="none"
        viewBox="0 0 16 34"
    >
        <path
            stroke="#E0E3E7"
            d="M0.499958 -6.55673e-07L0.499957 23.5C0.499957 29.0228 4.97711 33.5 10.5 33.5L15.5 33.5"
        />
    </svg>
);

/**
 * A vertical line to link the above "L" line for the PurchaseItems
 */
const VerticalLine = ({ count = 2 }) => {
    const additionalItemsCount = count - 1;
    const height = 85 + 30 * (additionalItemsCount - 1);
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="absolute left-[4.1px] top-[-36px] opacity-80"
            width="1"
            height={height}
            fill="none"
            viewBox={`0 0 1 ${height}`}
        >
            <path stroke="#E0E3E7" d={`M0.5 0L0.5 ${height}`} />
        </svg>
    );
};

const ItemDemographicSummary = ({ item }: { item: PurchaseItem }) => {
    const seller = getSeller();
    const isSameDemographic =
        isExperiencePurchaseItem(item) && item.guests.every((guest, _, guests) => guest.unit.id === guests[0].unit.id);

    const { data: demographic } = useResource<Unit>(["units", item.guests?.[0]?.unit?.id], null, {
        enabled: isSameDemographic && item.seller?.id === seller.id,
    });

    if (isGift(item)) {
        return null;
    }

    if (item.object === PurchaseItemObject.WARE) {
        return (
            <span className="text-sm" data-testid="purchase-list-item-total-quantity">
                <span className="mr-2 flex items-center">
                    <span
                        className={clsx(
                            "mr-1 h-2 w-2 rounded-full",
                            isMerchandisePurchaseItemFulfilled(item) ? "bg-success" : "bg-warning",
                        )}
                    />
                    <ShirtIcon className="mr-1" />
                    {item.quantity}
                </span>
            </span>
        );
    }

    if (isMembershipItem(item)) {
        return (
            <span className="text-sm" data-testid="purchase-list-item-total-quantity">
                <span className="mr-2">
                    <PassIcon className="mr-1" />
                    {item.quantity} {pluralize("Pass", item.quantity)}
                </span>
            </span>
        );
    }

    if (isEquipmentItem(item)) {
        return (
            <span className="text-sm">
                <span className="mr-2">
                    <EquipmentIcon className="mr-1" />
                    <NoTranslate>{item.quantity || 1}</NoTranslate>&nbsp;
                    <NoTranslate>{getCommonEquipmentLabel(item)}</NoTranslate>
                </span>
            </span>
        );
    }

    return (
        <span className="text-sm">
            {!isPackageItem(item) && isSameDemographic && demographic ? (
                <span className="mr-2">
                    <DemographicIcon className="mr-1" name={demographic.name} />
                    {item.quantity || 1}&nbsp;
                    <NoTranslate if={!isDefaultDemographic(demographic.name)}>{demographic.name}</NoTranslate>
                </span>
            ) : (
                <span className="mr-2">
                    <UserIcon className="mr-1" />
                    {item.quantity || 1} Reserved
                </span>
            )}
        </span>
    );
};

const getCommonEquipmentLabel = (item: PurchaseItem): string => {
    const names = (item.lineItems ?? [])
        .filter((lineItem) => lineItem.type === LineItemType.EQUIPMENT && lineItem.quantity)
        .map((lineItem) => lineItem.name)
        .filter(Boolean);
    return names.length === 1 ? names[0]! : "Reserved";
};
